import { enrolledUtilityProgramsCollection } from '@/reducers/enrolledUtilityPrograms'
import { useMemo } from 'react'
import { UtilityProgram } from '@/types/utilityProgram'
import { programsNewCollection, ProgramsNewModel } from '@/reducers/programsNew'
import getProgramEnrollmentExpiration from '@/utils/utility-program-helpers/getProgramEnrollmentExpiration'

/**
 * AVild - This hook exists to support the old single-program way of doing things.
 * The code Marti wrote for multi-programs and the endpoints it uses are the correct
 * way of doing things going forward - this hook and its usages exist because I
 * refactored old logic for the single-program flow and wanted a hook to re-use
 * logic between vehicle/charger/home screens.
 *
 * You should use useUtilityPrograms for any new multi-program logic.
 */
export default function useLegacyAvailablePrograms() {
  const { data: newPrograms, isLoadingOrIdle: loadingPotential } =
    programsNewCollection.useFetch()
  const {
    data: enrolledUtilityWithPrograms,
    isLoadingOrIdle: loadingEnrolled,
  } = enrolledUtilityProgramsCollection.useFetch()
  const enrolledPrograms = useMemo(() => {
    if (!enrolledUtilityWithPrograms) return []
    return enrolledUtilityWithPrograms.map((program) => program.utility_program)
  }, [enrolledUtilityWithPrograms])

  // Ensure we only show programs that are not enrolled in
  const eligiblePrograms = newPrograms
    .filter(
      (program: ProgramsNewModel) =>
        program.enrollment_overview === null &&
        Boolean(program.device_eligibility_overview?.profile?.is_eligible),
    )
    .filter((program) => {
      const { isExpired } = getProgramEnrollmentExpiration(
        program.utility_program.preferred_display_timezone,
        program.view_config_json?.programCard?.enrollmentEndDate,
      )

      return !isExpired
    })

  const programsNotEnrolledIn = eligiblePrograms.filter(
    (program: ProgramsNewModel) =>
      !enrolledPrograms
        .map((enrolledProgram: UtilityProgram) => enrolledProgram.id)
        .includes(program.id),
  )

  const firstAvailableProgram =
    (programsNotEnrolledIn?.[0] as ProgramsNewModel) ?? null

  return {
    firstAvailableProgram,
    programsAreAvailable: !!programsNotEnrolledIn.length,
    isLoading: loadingPotential || loadingEnrolled,
  }
}
